import {
  prepareWithSegments,
  layoutNextLine,
  type LayoutCursor,
  type PreparedTextWithSegments,
} from '@chenglou/pretext';

/** Named fonts only — Pretext warns `system-ui` is unreliable on macOS. */
export const BLOG_PRETEXT_BODY_FONT =
  '400 18px/29px "Google Sans Flex", "Google Sans", Arial, sans-serif';
const BODY_LINE_HEIGHT = 29;

const H1_FONT = '700 36px/40px "Google Sans Flex", "Google Sans", Arial, sans-serif';
const H1_LH = 40;
const H2_FONT = '700 30px/36px "Google Sans Flex", "Google Sans", Arial, sans-serif';
const H2_LH = 36;
const H3_FONT = '700 24px/30px "Google Sans Flex", "Google Sans", Arial, sans-serif';
const H3_LH = 30;
const H4_FONT = '700 20px/28px "Google Sans Flex", "Google Sans", Arial, sans-serif';
const H4_LH = 28;

const MARGIN = 20;
const GAP = 16;
export const BLOG_FLOAT_IMAGE_W = 240;
export const BLOG_FLOAT_IMAGE_H = 180;

export type BlogBlock =
  | { type: 'h1' | 'h2' | 'h3' | 'h4'; text: string }
  | { type: 'p'; text: string }
  | { type: 'image'; src: string; alt: string }
  | { type: 'ul' | 'ol'; items: string[] }
  | { type: 'blockquote'; text: string };

export type Obstacle = {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

export type LaidOutLine = {
  key: string;
  text: string;
  left: number;
  top: number;
  font: string;
  kind: 'body' | 'heading' | 'quote' | 'list';
};

export type LaidOutImage = {
  key: string;
  src: string;
  alt: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

export function markdownToPlain(s: string): string {
  return s
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseBlogBlocks(content: string): BlogBlock[] {
  if (!content || content.trim() === '') return [];

  const blocks = content.split(/\n\s*\n/);
  const out: BlogBlock[] = [];

  for (const block of blocks) {
    if (!block.trim()) continue;
    const lines = block.split('\n');

    const h1Match = lines[0].match(/^#\s+(.+)$/);
    if (h1Match) {
      out.push({ type: 'h1', text: h1Match[1].trim() });
      continue;
    }
    const h2Match = lines[0].match(/^##\s+(.+)$/);
    if (h2Match) {
      out.push({ type: 'h2', text: h2Match[1].trim() });
      continue;
    }
    const h3Match = lines[0].match(/^###\s+(.+)$/);
    if (h3Match) {
      out.push({ type: 'h3', text: h3Match[1].trim() });
      continue;
    }
    const h4Match = lines[0].match(/^####\s+(.+)$/);
    if (h4Match) {
      out.push({ type: 'h4', text: h4Match[1].trim() });
      continue;
    }

    if (lines.some((line) => line.match(/^[-*]\s+(.+)$/) || line.match(/^\d+\.\s+(.+)$/))) {
      const isOrdered = lines.some((line) => line.match(/^\d+\.\s+(.+)$/));
      const items: string[] = [];
      for (const line of lines) {
        const bulletMatch = line.match(/^[-*]\s+(.+)$/);
        const numberMatch = line.match(/^\d+\.\s+(.+)$/);
        if (bulletMatch) items.push(bulletMatch[1].trim());
        else if (numberMatch) items.push(numberMatch[1].trim());
      }
      if (items.length) out.push({ type: isOrdered ? 'ol' : 'ul', items });
      continue;
    }

    if (lines[0].match(/^>\s+(.+)$/)) {
      const quoteText = lines
        .map((line) => {
          const quoteMatch = line.match(/^>\s+(.+)$/);
          return quoteMatch ? quoteMatch[1] : line;
        })
        .join('\n');
      out.push({ type: 'blockquote', text: quoteText.trim() });
      continue;
    }

    const imageMatch = lines[0].match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      out.push({ type: 'image', alt: imageMatch[1] || '', src: imageMatch[2] });
      continue;
    }

    out.push({ type: 'p', text: lines.join('\n').trim() });
  }

  return out;
}

function lineSlotAtY(
  y: number,
  lineHeight: number,
  obstacles: Obstacle[],
  contentWidth: number,
  margin: number
): { left: number; width: number } {
  const midY = y + lineHeight / 2;
  let left = margin;
  const right = contentWidth - margin;

  for (const ob of obstacles) {
    if (midY >= ob.top && midY < ob.top + ob.height) {
      const textStart = ob.left + ob.width + GAP;
      left = Math.max(left, textStart);
    }
  }

  const width = Math.max(80, right - left);
  return { left, width };
}

function headingFont(level: 'h1' | 'h2' | 'h3' | 'h4'): { font: string; lh: number } {
  switch (level) {
    case 'h1':
      return { font: H1_FONT, lh: H1_LH };
    case 'h2':
      return { font: H2_FONT, lh: H2_LH };
    case 'h3':
      return { font: H3_FONT, lh: H3_LH };
    default:
      return { font: H4_FONT, lh: H4_LH };
  }
}

function layoutTextBlock(
  plain: string,
  font: string,
  lineHeight: number,
  startY: number,
  obstacles: Obstacle[],
  contentWidth: number,
  keyPrefix: string,
  kind: LaidOutLine['kind']
): { lines: LaidOutLine[]; endY: number } {
  const lines: LaidOutLine[] = [];
  if (!plain) return { lines, endY: startY };

  let prepared: PreparedTextWithSegments;
  try {
    prepared = prepareWithSegments(plain, font);
  } catch {
    return { lines, endY: startY };
  }

  let cursor: LayoutCursor = { segmentIndex: 0, graphemeIndex: 0 };
  let y = startY;
  let lineIndex = 0;

  while (true) {
    const { left, width } = lineSlotAtY(y, lineHeight, obstacles, contentWidth, MARGIN);
    const line = layoutNextLine(prepared, cursor, width);
    if (!line) break;
    lines.push({
      key: `${keyPrefix}-${lineIndex++}`,
      text: line.text,
      left,
      top: y,
      font,
      kind,
    });
    y += lineHeight;
    cursor = line.end;
  }

  return { lines, endY: y };
}

/**
 * JS + Pretext layout: absolute lines + float-left images as obstacles (same idea as chenglou.me pretext demos).
 */
export function computeBlogPretextLayout(
  content: string,
  contentWidth: number
): { lines: LaidOutLine[]; images: LaidOutImage[]; height: number } {
  const blocks = parseBlogBlocks(content);
  const lines: LaidOutLine[] = [];
  const images: LaidOutImage[] = [];
  let obstacles: Obstacle[] = [];
  let y = 0;
  let imgCounter = 0;
  let blockIdx = 0;

  const pruneObstacles = (cursorY: number) => {
    obstacles = obstacles.filter((ob) => cursorY < ob.top + ob.height + GAP);
  };

  for (const block of blocks) {
    pruneObstacles(y);

    if (block.type === 'image') {
      const id = `img-${imgCounter++}`;
      const ob: Obstacle = {
        id,
        left: MARGIN,
        top: y,
        width: BLOG_FLOAT_IMAGE_W,
        height: BLOG_FLOAT_IMAGE_H,
      };
      obstacles.push(ob);
      images.push({
        key: id,
        src: block.src,
        alt: block.alt,
        left: ob.left,
        top: ob.top,
        width: ob.width,
        height: ob.height,
      });
      blockIdx++;
      continue;
    }

    if (block.type === 'h1' || block.type === 'h2' || block.type === 'h3' || block.type === 'h4') {
      y += y > 0 ? 24 : 0;
      const { font, lh } = headingFont(block.type);
      const plain = markdownToPlain(block.text);
      const { lines: ls, endY } = layoutTextBlock(
        plain,
        font,
        lh,
        y,
        obstacles,
        contentWidth,
        `h-${blockIdx}`,
        'heading'
      );
      lines.push(...ls);
      y = endY + 20;
      blockIdx++;
      continue;
    }

    if (block.type === 'blockquote') {
      y += 8;
      const plain = markdownToPlain(block.text);
      const { lines: ls, endY } = layoutTextBlock(
        plain,
        BLOG_PRETEXT_BODY_FONT,
        BODY_LINE_HEIGHT,
        y,
        obstacles,
        contentWidth,
        `q-${blockIdx}`,
        'quote'
      );
      lines.push(...ls);
      y = endY + 16;
      blockIdx++;
      continue;
    }

    if (block.type === 'ul' || block.type === 'ol') {
      y += 8;
      let itemIdx = 0;
      for (const item of block.items) {
        const prefix = block.type === 'ul' ? '• ' : `${itemIdx + 1}. `;
        const plain = prefix + markdownToPlain(item);
        const { lines: ls, endY } = layoutTextBlock(
          plain,
          BLOG_PRETEXT_BODY_FONT,
          BODY_LINE_HEIGHT,
          y,
          obstacles,
          contentWidth,
          `li-${blockIdx}-${itemIdx}`,
          'list'
        );
        lines.push(...ls);
        y = endY + 8;
        itemIdx++;
      }
      y += 8;
      blockIdx++;
      continue;
    }

    // paragraph
    y += y > 0 ? 12 : 0;
    const plain = markdownToPlain(block.text);
    const { lines: ls, endY } = layoutTextBlock(
      plain,
      BLOG_PRETEXT_BODY_FONT,
      BODY_LINE_HEIGHT,
      y,
      obstacles,
      contentWidth,
      `p-${blockIdx}`,
      'body'
    );
    lines.push(...ls);
    y = endY + 16;
    blockIdx++;
  }

  let height = y + MARGIN;
  for (const img of images) {
    height = Math.max(height, img.top + img.height + GAP + MARGIN);
  }

  return { lines, images, height: Math.max(height, 120) };
}
